import AppFormContainer from '@nxsys/components/AppFormContainer';
import { StyledAppSelect } from '@nxsys/components/AppSelect/index.styled';
import { Form } from 'antd';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import StepCard from './StepCard';
import { getHolidayPaymentMethodOnboardingOptions } from '@nxsys/constants/AppConstants';
import { HolidayMethodEnum, PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

interface AddHolidayPayMethodProps {
  stepNumber: number;
  holidayPaymentMethod?: string | null;
  // The prospect's own payment model(s), e.g. ['PAYEE'] — the 8% irregular
  // method (NX-6284) is Ireland-PAYE-only; the backend 400s an umbrella or
  // RCT prospect who picks it (onboarding.service.ts).
  paymentModel?: string[] | string | null;
  completed?: boolean;
  onSubmitStep: (values: any) => void;
}

type AddHolidayPayMethodValues = {
  holidayPaymentMethodValue: string;
};

const AddHolidayPayMethod: React.FC<AddHolidayPayMethodProps> = ({
  stepNumber,
  holidayPaymentMethod,
  paymentModel,
  completed = false,
  onSubmitStep,
}) => {
  const [form] = Form.useForm<AddHolidayPayMethodValues>();
  const { messages } = useIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const isPAYE = Array.isArray(paymentModel)
    ? paymentModel.includes(PAYMENT_MODEL_ENUM.PAYEE)
    : paymentModel === PAYMENT_MODEL_ENUM.PAYEE;

  // The onboarding option list already applies the Ireland rule (no 52 Week
  // Average); the irregular method additionally needs the payment-model rule,
  // which that shared list has no payment-model input to apply itself.
  const options = useMemo(() => {
    const base = getHolidayPaymentMethodOnboardingOptions(
      isIreland,
      holidayPaymentMethod,
    );
    return isPAYE
      ? base
      : base.filter(
          (o) => o.value !== HolidayMethodEnum.IRREGULAR_HOURS_8_PERCENT,
        );
  }, [isIreland, isPAYE, holidayPaymentMethod]);

  const onFinish = (values: AddHolidayPayMethodValues) => {
    const params = {
      holidayPayMethod: values?.holidayPaymentMethodValue ?? null,
    };
    onSubmitStep(params);
  };

  return (
    <StepCard
      title={messages['prospect.onboarding.addHolidayPayMethod'] as string}
      stepNumber={stepNumber}
      completed={completed}
      actions={[
        {
          variant: 'outlined',
          name: messages['common.save'] as string,
          onClick: () => form.submit(),
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        initialValues={{
          holidayPaymentMethodValue: holidayPaymentMethod ?? undefined,
        }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
      >
        <Form.Item
          label={messages['prospect.onboarding.holidayPaymentMethod'] as string}
          name="holidayPaymentMethodValue"
          rules={[
            {
              required: true,
              message: messages['form.pleaseSelect'] as string,
            },
          ]}
        >
          <StyledAppSelect
            options={options}
            placeholder={
              messages[
                'prospect.onboarding.holidayPaymentMethod.placeholder'
              ] as string
            }
            showSearch
          />
        </Form.Item>
      </AppFormContainer>
    </StepCard>
  );
};

export default AddHolidayPayMethod;
